import { useState, useEffect } from 'react';
import { getUTMParamsForSubmit } from '../../utils/utm';

interface ContactFormData {
  name: string;
  phone: string;
  interest: string;
  startTime: string;
  message: string;
}

interface ContactFormModalProps {
  onSuccess?: () => void;
  onPartialDataChange?: (hasData: boolean) => void;
  onAdditionalFieldsChange?: (showFields: boolean) => void;
  onCompletedAdditionalFieldsChange?: (completed: boolean) => void;
}

export default function ContactFormModal({ onSuccess, onPartialDataChange, onAdditionalFieldsChange, onCompletedAdditionalFieldsChange }: ContactFormModalProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    interest: '',
    startTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Revelar campos adicionais quando nome e telefone estiverem preenchidos
  useEffect(() => {
    // Não executar se formulário foi enviado com sucesso
    if (submitStatus === 'success') {
      return;
    }
    
    const isNameValid = formData.name.trim().length >= 3;
    const isPhoneValid = formData.phone.replace(/\D/g, '').length >= 10;
    
    // Notificar componente pai sobre dados parciais
    if (onPartialDataChange) {
      onPartialDataChange(isNameValid && isPhoneValid);
    }
    
    if (isNameValid && isPhoneValid && !showAdditionalFields) {
      setShowAdditionalFields(true);
      if (onAdditionalFieldsChange) {
        onAdditionalFieldsChange(true);
      }
      // Scroll suave para os novos campos após animação
      setTimeout(() => {
        document.getElementById('additional-fields')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 400);
    } else if ((!isNameValid || !isPhoneValid) && showAdditionalFields) {
      setShowAdditionalFields(false);
      if (onAdditionalFieldsChange) {
        onAdditionalFieldsChange(false);
      }
    }
  }, [formData.name, formData.phone, showAdditionalFields, submitStatus, onPartialDataChange, onAdditionalFieldsChange]);

  // Monitorar se campos adicionais foram preenchidos
  useEffect(() => {
    const hasInterest = formData.interest.trim().length > 0;
    const hasStartTime = formData.startTime.trim().length > 0;
    
    if (onCompletedAdditionalFieldsChange) {
      onCompletedAdditionalFieldsChange(hasInterest && hasStartTime);
    }
  }, [formData.interest, formData.startTime, onCompletedAdditionalFieldsChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Se for o campo de telefone, permitir apenas números
    if (name === 'phone') {
      const numbersOnly = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (!formData.name.trim() || !formData.phone.trim()) {
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      const conversionDate = new Date().toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const utmParams = getUTMParamsForSubmit();

      const dataToSend = {
        api_key: "582121bf-6661-4c70-81e0-f180f481a92b",
        nome: formData.name.trim(),
        telefone: `+55${formData.phone.replace(/\D/g, '')}`,
        origem: "landing_page",
        data_conversao: conversionDate,
        interesse: formData.interest || 'Não informado',
        quando_pretende_comecar: formData.startTime || 'Não informado',
        mensagem: formData.message.trim() || "Sem mensagem adicional",
        ...utmParams
      };

      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        console.log('Ambiente local detectado. Dados que seriam enviados:', dataToSend);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          interest: '',
          startTime: '',
          message: ''
        });
        if (onSuccess) onSuccess();
        return;
      }

      console.log('Enviando dados para M4Track:', dataToSend);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      try {
        const response = await fetch('https://webhooks.manager01.m4track.com.br/webhook/vox2you-maceio', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(dataToSend),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        
        console.log('M4Track: Resposta recebida', response.status);
        
        // Se a requisição foi enviada e recebemos uma resposta (mesmo que não seja 2xx), considerar sucesso
        // O webhook do M4Track pode retornar diferentes status codes mas ainda processar o lead
        if (response.status >= 200 && response.status < 500) {
          console.log('M4Track: Formulário enviado com sucesso');
          setSubmitStatus('success');
          setIsSubmitting(false);
          // Não limpar formData para manter estado do formulário
          // Não fechar o modal automaticamente - deixar usuário ver a mensagem
          // if (onSuccess) onSuccess();
        } else {
          console.error('M4Track: Erro no servidor', response.status, response.statusText);
          setSubmitStatus('error');
          setIsSubmitting(false);
        }
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('Requisição cancelada por timeout');
        } else {
          console.error('Erro na requisição:', error);
        }
        setSubmitStatus('error');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  // Renderizar modal de sucesso se formulário foi enviado
  if (submitStatus === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-4 sm:py-8 px-2 sm:px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full flex items-center justify-center mb-3 sm:mb-4 animate-bounce">
              <i className="ri-check-line text-white text-3xl sm:text-4xl"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2 px-2">
              Formulário recebido com sucesso!
            </h3>
            <p className="text-sm sm:text-base text-gray-600 text-center px-2">
              Obrigado por seu interesse na Vox2You!
            </p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4 sm:p-6 border-2 border-green-200 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <i className="ri-whatsapp-fill text-green-600 text-xl sm:text-2xl mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-gray-900 font-bold text-base sm:text-lg mb-1 sm:mb-2">
                  Nossa equipe entrará em contato
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Em breve você receberá uma mensagem via <strong>WhatsApp</strong> e/ou uma <strong>ligação</strong> de nossa equipe para passar todas as informações sobre nossos programas e tirar suas dúvidas.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onSuccess}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base sm:text-lg"
          >
            <i className="ri-check-line text-xl sm:text-2xl"></i>
            Entendi, obrigado!
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 2000px;
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.5s ease-out forwards;
        }
      `}</style>
      
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <i className="ri-user-line text-gray-500 text-sm sm:text-base"></i>
              Nome completo
            </span>
            {formData.name.trim().length >= 3 && (
              <i className="ri-check-line text-green-500 text-lg sm:text-xl"></i>
            )}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            placeholder="Digite seu nome completo"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-1.5 sm:gap-2">
              <i className="ri-whatsapp-line text-gray-500 text-sm sm:text-base"></i>
              WhatsApp
            </span>
            {formData.phone.replace(/\D/g, '').length >= 10 && (
              <i className="ri-check-line text-green-500 text-lg sm:text-xl"></i>
            )}
          </label>
          <div className="flex gap-1.5 sm:gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg bg-gray-50">
              <span className="text-base sm:text-lg">🇧🇷</span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">+55</span>
            </div>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="11999999999"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Digite apenas números (DDD + número)</p>
        </div>

        {!showAdditionalFields && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
              <i className="ri-arrow-down-line animate-bounce"></i>
              Preencha os campos acima para continuar
            </p>
          </div>
        )}

        {showAdditionalFields && (
          <div id="additional-fields" className="animate-slideDown space-y-3 sm:space-y-4">
            <div className="pt-3 sm:pt-4 border-t border-gray-200">
              <p className="text-xs sm:text-sm text-green-600 font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                <i className="ri-check-double-line"></i>
                Ótimo! Agora mais algumas informações...
              </p>
            </div>

            <div>
        <label htmlFor="interest" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-star-line text-gray-500 text-sm sm:text-base"></i>
          Interesse Principal
        </label>
        <select
          id="interest"
          name="interest"
          value={formData.interest}
          onChange={handleInputChange}
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
        >
          <option value="">Selecione uma opção</option>
          <option value="performance-corporativa">Aumentar Performance Corporativa</option>
          <option value="eliminar-medo-tensao">Eliminar Medo/Tensão ao falar em público</option>
          <option value="palestras-apresentacoes">Ministrar Palestras/Apresentações de impacto</option>
          <option value="videos-atrativos">Engajar com vídeos mais atrativos</option>
          <option value="outro-motivo">Outro motivo</option>
        </select>
      </div>

      <div>
        <label htmlFor="startTime" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-calendar-line text-gray-500 text-sm sm:text-base"></i>
          Quando pretende começar?
        </label>
        <select
          id="startTime"
          name="startTime"
          value={formData.startTime}
          onChange={handleInputChange}
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
        >
          <option value="">Selecione uma opção</option>
          <option value="urgente">Urgente (o quanto antes)</option>
          <option value="proximo-mes">Para o próximo mês</option>
          <option value="ate-3-meses">Em até 3 meses</option>
          <option value="so-pesquisando">Só pesquisando, meu desenvolvimento não é prioridade</option>
        </select>
      </div>

      <div>
        <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2">
          <i className="ri-message-3-line text-gray-500 text-sm sm:text-base"></i>
          Mensagem (opcional)
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={3}
          maxLength={500}
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
          placeholder="Conte-nos mais sobre seus objetivos..."
        ></textarea>
      </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base sm:text-lg shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="ri-whatsapp-line text-xl"></i>
                  Enviar para WhatsApp
                </>
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
                <div>
                  <p className="text-red-800 font-semibold">Erro ao enviar mensagem</p>
                  <p className="text-red-700 text-sm">Por favor, tente novamente.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </>
  );
}
